import { isNil } from 'lodash';
import { CSSProperties, useCallback } from 'react';
import { ScrollView } from 'react-native';
import { View } from '@shared/components/View';
import { SupportedSubformComponentValue } from '@shared/components/blocks/types';
import { MBModalOrMobile } from '@shared/components/modalOrMobile';
import { NavHeader } from '@shared/components/nav';
import { AuditCheckboxField } from '@shared/data/entity/fields/AuditCheckboxField';
import { AuditTagField } from '@shared/data/entity/fields/AuditTagField';
import { AuditTextField } from '@shared/data/entity/fields/AuditTextField';
import { castField } from '@shared/data/entity/fields/Field';
import { ResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { AuditCheckboxEditField } from '@shared/scenes/entity/components/fields/AuditCheckboxEditField';
import { AuditTagEditField } from '@shared/scenes/entity/components/fields/AuditTagEditField';
import { AuditTextEditField } from '@shared/scenes/entity/components/fields/AuditTextEditField';
import { StaticTextEditField } from '@shared/scenes/entity/components/fields/StaticTextEditField';
import { SubformData } from '@shared/scenes/entity/components/fields/Subform/useSubformData';
import { FieldRecord } from '@shared/scenes/entity/types';
import { getFieldError } from '@shared/scenes/entity/util/getFieldError';
import { PAGE_MODAL_WIDTH, grid } from '@shared/styles';
import { Colors } from '@shared/styles/colors';
import { API } from '@shared/types/api';
import {
  isAuditCheckboxComponent,
  isAuditTagComponent,
  isAuditTextComponent,
  isStaticTextComponent,
} from '../../utils';

interface Props extends SubformData {
  onSubmit: (params: {
    data: Record<string, SupportedSubformComponentValue | null>;
    wasValid: boolean;
  }) => void;
  isReadOnly?: boolean;
}

export function SubformView({
  workflowTemplate,
  components,
  orderedComponents,
  fields,
  updateField,
  errors,
  isValid,
  setSubmitAttempted,
  onSubmit,
  isReadOnly,
}: Props) {
  const handleBack = useCallback(() => {
    setSubmitAttempted(true);
    const payload: Record<string, SupportedSubformComponentValue | null> = {};
    for (const component of components) {
      if (isStaticTextComponent(component)) {
        continue;
      }
      const field = fields[component.id];
      if (!isNil(field) && !field.isEmpty) {
        payload[component.id] = field.toFieldJson();
      } else {
        payload[component.id] = null;
      }
    }
    onSubmit({ data: payload, wasValid: isValid });
  }, [isValid, components, fields, onSubmit, setSubmitAttempted]);

  return (
    <MBModalOrMobile
      desktopModalWidth={PAGE_MODAL_WIDTH}
      desktopModalHeight={'calc(95vh - 76px)'}
      title={workflowTemplate?.name}
      onBack={handleBack}
      paddingOnWeb={0}
      headerOnMobile={<NavHeader title={workflowTemplate?.name} onPressLeft={handleBack} />}
    >
      <ResponsiveFormContext.Provider value={true}>
        {/*
         * The gray background is on the ScrollView itself (not an inner wrapper)
         * so it fills the full scroll area and never scrolls away with content.
         * contentContainerStyle flexGrow:1 ensures it fills the visible modal
         * height even when there are few fields.
         */}
        <ScrollView
          style={scrollContainerStyle}
          contentContainerStyle={scrollContentStyle}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <View style={fieldContainerStyle}>
            {orderedComponents.map(component => (
              <SubformField
                key={component.id}
                component={component}
                fields={fields}
                updateField={updateField}
                error={getFieldError(errors, component.id)}
                isReadOnly={isReadOnly}
              />
            ))}
          </View>
        </ScrollView>
      </ResponsiveFormContext.Provider>
    </MBModalOrMobile>
  );
}

interface SubformFieldProps {
  component: API.AnyComponent;
  fields: FieldRecord;
  updateField: (
    componentId: string,
    field: AuditTagField | AuditTextField | AuditCheckboxField | null,
  ) => void;
  error?: string;
  isReadOnly?: boolean;
}

function SubformField({ component, fields, updateField, error, isReadOnly }: SubformFieldProps) {
  if (isAuditTagComponent(component)) {
    return (
      <AuditTagEditField
        component={component}
        field={castField(fields[component.id], AuditTagField)}
        updateField={isReadOnly ? undefined : updateField}
        error={error}
        style={fieldStyle}
      />
    );
  }
  if (isAuditTextComponent(component)) {
    return (
      <AuditTextEditField
        component={component}
        field={castField(fields[component.id], AuditTextField)}
        updateField={isReadOnly ? undefined : updateField}
        error={error}
        style={fieldStyle}
      />
    );
  }
  if (isAuditCheckboxComponent(component)) {
    return (
      <AuditCheckboxEditField
        component={component}
        field={castField(fields[component.id], AuditCheckboxField)}
        updateField={isReadOnly ? undefined : updateField}
        error={error}
        style={fieldStyle}
      />
    );
  }
  if (isStaticTextComponent(component)) {
    return <StaticTextEditField component={component} style={fieldStyle} />;
  }
  return null;
}

const scrollContainerStyle = {
  flex: 1,
  backgroundColor: Colors.v2.backgroundGrayLight,
  paddingTop: grid(1),
  paddingBottom: grid(4),
  borderTopWidth: 1,
  borderTopColor: Colors.v2.gray10,
};

const scrollContentStyle = {
  flexGrow: 1,
  paddingTop: grid(1),
  paddingBottom: grid(4),
  paddingLeft: grid(1),
  paddingRight: grid(1),
};

const fieldContainerStyle = {
  width: '100%',
  maxWidth: 456,
  alignSelf: 'center' as const,
};

// Matches EntityFormResponsive's formComponent + formComponentStandalone spacing.
const fieldStyle: CSSProperties = {
  paddingTop: grid(0.25),
  paddingBottom: grid(1.75),
  marginLeft: grid(2),
  marginRight: grid(2),
};
